import React, { useCallback, useMemo } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'

import Node, { InputType } from 'components_new/molecules/Node'
import AddButton from 'components_new/molecules/Node/AddButton'
import Caption from 'components_new/molecules/Node/Caption'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import NameField from 'components_new/molecules/Node/NameField'
import SideBadge from 'components_new/molecules/Node/SideBadge'

import NodeType from 'types/transformation/enums/NodeType'
import {
  NodeSchema,
  UniteNode,
  Node as TNode
} from 'types/transformation/types/nodes'

import {
  pruneRef,
  useColumnOrigins,
  useInputSchemas,
  useIsInvalid,
  usePruneRefs
} from '../hooks'

import { getNewColumn } from './utils'

interface UniteNodeProps {
  id: string
  data: UniteNode
  selected: boolean
}

const Unite = (props: UniteNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchemas = useInputSchemas({
    left: data.left_node,
    right: data.right_node
  })
  const originByNode = useColumnOrigins()

  const toFieldOptions = useCallback(
    (schema: NodeSchema[]): FieldOption[] =>
      schema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [originByNode]
  )

  const leftOptions = useMemo(
    () => toFieldOptions(inputSchemas.left),
    [toFieldOptions, inputSchemas.left]
  )
  const rightOptions = useMemo(
    () => toFieldOptions(inputSchemas.right),
    [toFieldOptions, inputSchemas.right]
  )

  // input schema has changed, validate references
  usePruneRefs<UniteNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      schema: nodeData.schema.map((col) => ({
        ...col,
        left_column: pruneRef(col.left_column, inputSchemas.left),
        right_column: pruneRef(col.right_column, inputSchemas.right)
      }))
    }),
    [inputSchemas.left, inputSchemas.right]
  )

  const handleSelectColumn = useCallback(
    (
      columnIndex: number,
      columnId: string,
      refColumn: 'left_column' | 'right_column'
    ) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as UniteNode

          newNodeData.schema[columnIndex][refColumn] = columnId

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  const handleChangeName = useCallback(
    (columnIndex: number, name: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as UniteNode

          newNodeData.schema[columnIndex].name = name

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  const handleAdd = useCallback(() => {
    setNodes((existingNodes) =>
      existingNodes.map((node) => {
        if (node.id !== id) {
          return node
        }

        const newNodeData = cloneDeep(node.data) as UniteNode

        newNodeData.schema.push(getNewColumn(newNodeData))

        return {
          ...node,
          data: newNodeData
        }
      })
    )
  }, [setNodes, id])

  const handleRemove = useCallback(
    (columnId: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as UniteNode

          newNodeData.schema = newNodeData.schema.filter(
            (col) => col.id !== columnId
          )

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="DeviceHubOutlined"
      inputType={InputType.DOUBLE}
      title={'Unite'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.UNITE}
    >
      <Box sx={{ width: '300px' }}>
        {data.schema.map((column, index) => (
          <Box key={column.id}>
            {index > 0 ? <Divider gutter /> : null}

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Caption>Källkolumner</Caption>
              <IconButton
                onClick={() => handleRemove(column.id)}
                size="small"
                sx={{ mb: 0.5 }}
              >
                <Icon name="Close" sx={{ fontSize: '15px' }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '7px',
                mb: 0.5
              }}
            >
              <SideBadge label="L" />
              <FieldSelect
                value={column.left_column}
                options={leftOptions}
                onChange={(val) =>
                  handleSelectColumn(index, val, 'left_column')
                }
                placeholder="Välj attribut"
                compact
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: '7px' }}>
              <SideBadge label="R" />
              <FieldSelect
                value={column.right_column}
                options={rightOptions}
                onChange={(val) =>
                  handleSelectColumn(index, val, 'right_column')
                }
                placeholder="Välj attribut"
                compact
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Caption>Nytt kolumnnamn</Caption>
            </Box>
            <NameField
              value={column.name}
              onChange={(name) => handleChangeName(index, name)}
            />
          </Box>
        ))}

        <Box sx={{ mt: 1 }}>
          <AddButton onClick={handleAdd} />
        </Box>
      </Box>
    </Node>
  )
}

export default Unite
